import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
    acceptChat,
    closeChat,
    declineChat,
    getChatSession,
    getConversationMessages,
    getConversations,
    markChatRead,
} from "../../Services/Chat";
import { getAllUsers } from "../../Services/User";
import {
    getSenderId,
    getMessageParticipantId,
    joinChatRoom,
    setChatPageContext,
    clearChatPageContext,
    subscribeNewMessages,
    subscribeChatStatus,
} from "../../Utils/chatSocket";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { resolveImageUrl } from "../../Utils/helpers";
import { adminSwalOptions } from "../../Utils/adminSwal";
import { Error as ErrorAlert } from "../../Components/Modal";
import PageHeader from "../../Components/PageHeader";
import ChatList from "./List";
import ChatMessage from "./Message";
import "./style.css";

const AVATAR_FALLBACK = "images/userImage.png";
const MOBILE_BREAKPOINT = 992;

const QUEUE_TABS = [
    { id: "pending", label: "Requests" },
    { id: "active", label: "Live" },
    { id: "all", label: "All" },
];
const ROLE_TABS = [
    { id: "all", label: "All" },
    { id: "users", label: "Users" },
    { id: "movers", label: "Drivers" },
];

const DECLINE_REASONS = {
    resolved: "Your issue has already been resolved",
    email: "Please email us for this request so we can look into it",
    ride_chat: "Please use the in-app ride chat with your driver",
    later: "Our team is busy right now, please request a chat again later",
    other: "Other…",
};

const getConversationName = (item) => {
    const user = item?.user;
    if (!user) return "";
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
    return fullName || user.firstName || "";
};

const getParticipantId = (item) =>
    (item?.receiverId || item?.user?._id)?.toString();

const roleFilterFor = (roleTab) =>
    roleTab === "users" ? "user" : roleTab === "movers" ? "driver" : undefined;

const buildContactList = (participants = [], conversations = [], sortByQueue = false) => {
    const byId = new Map();

    participants.forEach((userDoc) => {
        const id = userDoc?._id?.toString();
        if (!id) return;
        byId.set(id, {
            receiverId: id,
            user: userDoc,
            status: "none",
            lastMessage: "",
            lastMessageDate: null,
            unreadCount: 0,
        });
    });

    conversations.forEach((conversation) => {
        const id = getParticipantId(conversation);
        if (!id) return;
        const existing = byId.get(id);
        byId.set(id, {
            ...(existing || {}),
            ...conversation,
            receiverId: id,
            user: conversation.user || existing?.user,
        });
    });

    const list = Array.from(byId.values());
    // The request queue is first come, first served (server order).
    if (sortByQueue) return list;

    return list.sort((a, b) => {
        const unreadDiff = (b.unreadCount || 0) - (a.unreadCount || 0);
        if (unreadDiff) return unreadDiff;

        const dateA = a.lastMessageDate ? new Date(a.lastMessageDate).getTime() : 0;
        const dateB = b.lastMessageDate ? new Date(b.lastMessageDate).getTime() : 0;
        if (dateB !== dateA) return dateB - dateA;

        return getConversationName(a).localeCompare(getConversationName(b));
    });
};

const mergeMessages = (current, incoming) => {
    const byId = new Map();
    [...current, ...incoming].forEach((m) => {
        if (m?._id) byId.set(String(m._id), m);
    });
    return Array.from(byId.values()).sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
};

export default function Chat() {
    const { user } = useSelector((state) => state?.slice);
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [roleTab, setRoleTab] = useState("all");
    const [queueTab, setQueueTab] = useState("pending");
    const [chatTab, setChatTab] = useState("");
    const [selectedItem, setSelectedItem] = useState(null);
    const [chat, setChat] = useState([]);
    const [hasOlder, setHasOlder] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [actionBusy, setActionBusy] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [mobilePanel, setMobilePanel] = useState("list");
    const [isMobileView, setIsMobileView] = useState(
        () => typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT
    );
    const chatTabRef = useRef(chatTab);
    const adminIdRef = useRef(user?._id?.toString());

    const roleFilter = roleFilterFor(roleTab);
    const statusFilter = queueTab === "all" ? undefined : queueTab;

    useEffect(() => {
        chatTabRef.current = chatTab;
    }, [chatTab]);

    useEffect(() => {
        adminIdRef.current = user?._id?.toString();
    }, [user?._id]);

    useEffect(() => {
        const state = location.state;
        if (!state?.participantId) return;

        setRoleTab(state.chatTab === "movers" ? "movers" : "users");
        setQueueTab("all");
        setChat([]);
        setChatTab(String(state.participantId));
        if (typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT) {
            setMobilePanel("chat");
        }
        navigate(location.pathname, { replace: true, state: null });
    }, [location.state, location.pathname, navigate]);

    useEffect(() => {
        const adminId = user?._id?.toString();
        if (adminId) joinChatRoom(adminId);
    }, [user?._id]);

    useEffect(() => {
        setChatPageContext(chatTab);
        return () => clearChatPageContext();
    }, [chatTab]);

    useEffect(() => {
        const onResize = () => {
            const mobile = window.innerWidth < MOBILE_BREAKPOINT;
            setIsMobileView(mobile);
            if (!mobile) setMobilePanel("list");
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const refreshLists = useCallback(() => {
        queryClient.invalidateQueries(["get-conversations"]);
        queryClient.invalidateQueries(["unreadChatCount"]);
    }, [queryClient]);

    const { data: sessionData } = useQuery(
        ["support-session", chatTab],
        () => getChatSession(chatTab),
        { enabled: !!chatTab, refetchOnWindowFocus: false }
    );
    const session = sessionData?.data?.data;

    const setSession = useCallback(
        (participantId, next) => {
            if (!participantId || !next) return;
            queryClient.setQueryData(["support-session", String(participantId)], (old) => ({
                ...(old || {}),
                data: { ...(old?.data || {}), data: next },
            }));
        },
        [queryClient]
    );

    // First page of the selected conversation (opening it also marks the customer's messages as read).
    useEffect(() => {
        if (!chatTab) {
            setChat([]);
            setHasOlder(false);
            return undefined;
        }
        let cancelled = false;
        setChat([]);
        setHasOlder(false);
        setLoadingMessages(true);
        getConversationMessages(chatTab)
            .then((res) => {
                if (cancelled || chatTabRef.current !== chatTab) return;
                setChat((res?.data?.data ?? []).slice().reverse());
                setHasOlder(Boolean(res?.data?.pagination?.hasNextPage));
                if (res?.data?.session) setSession(chatTab, res.data.session);
                refreshLists();
            })
            .catch(() => {
                if (!cancelled) setChat([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingMessages(false);
            });
        return () => {
            cancelled = true;
        };
    }, [chatTab, refreshLists, setSession]);

    const loadOlder = useCallback(async () => {
        const participantId = chatTabRef.current;
        const oldest = chat[0]?._id;
        if (!participantId || !oldest || loadingOlder) return;
        setLoadingOlder(true);
        try {
            const res = await getConversationMessages(participantId, oldest);
            if (chatTabRef.current !== participantId) return;
            setChat((prev) => mergeMessages(prev, res?.data?.data ?? []));
            setHasOlder(Boolean(res?.data?.pagination?.hasNextPage));
        } catch (err) {
            ErrorAlert(err?.response?.data?.message || "Could not load older messages.");
        } finally {
            setLoadingOlder(false);
        }
    }, [chat, loadingOlder]);

    useEffect(() => {
        const unsubscribeMessages = subscribeNewMessages((newMsg) => {
            const participantId = getMessageParticipantId(newMsg);
            const fromCustomer = getSenderId(newMsg) !== adminIdRef.current;

            if (participantId && participantId === chatTabRef.current) {
                setChat((prev) => mergeMessages(prev, [newMsg]));
                if (fromCustomer && newMsg?.type !== "system") {
                    markChatRead(participantId).finally(refreshLists);
                    return;
                }
            }
            refreshLists();
        });

        const unsubscribeStatus = subscribeChatStatus((next) => {
            const participantId = next?.userId?.toString();
            if (participantId) setSession(participantId, next);
            refreshLists();
        });

        return () => {
            unsubscribeMessages();
            unsubscribeStatus();
        };
    }, [refreshLists, setSession]);

    const {
        isLoading: conversationLoading,
        data: conversationData,
    } = useQuery(
        ["get-conversations", roleTab, queueTab],
        () => getConversations(roleFilter, statusFilter),
        { refetchOnWindowFocus: false, staleTime: 15 * 1000 }
    );

    // "All" also lists customers/drivers who never chatted, so admin can start a chat.
    const {
        isLoading: participantsLoading,
        data: participantsData,
    } = useQuery(
        ["chat-participants", roleTab],
        async () => {
            const roles = roleFilter ? [roleFilter] : ["user", "driver"];
            const results = await Promise.all(
                roles.map((role) => getAllUsers(1, 1000, role, "", "", "", ""))
            );
            return results.flatMap((res) => res?.data?.data?.docs ?? []);
        },
        {
            enabled: queueTab === "all",
            refetchOnWindowFocus: false,
            staleTime: 60 * 1000,
        }
    );

    const counts = conversationData?.data?.counts ?? {};

    const contactList = useMemo(
        () =>
            buildContactList(
                queueTab === "all" ? participantsData ?? [] : [],
                conversationData?.data?.data ?? [],
                queueTab === "pending"
            ),
        [participantsData, conversationData, queueTab]
    );

    const listLoading = conversationLoading || (queueTab === "all" && participantsLoading);

    const filteredConversations = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return contactList;

        return contactList.filter((item) => {
            const name = getConversationName(item).toLowerCase();
            const lastMessage = (item?.lastMessage || "").toLowerCase();
            const topic = (item?.topic || "").toLowerCase();
            const email = (item?.user?.email || "").toLowerCase();
            const phone = (item?.user?.phone || item?.user?.mobile || "").toString();

            return (
                name.includes(query) ||
                lastMessage.includes(query) ||
                topic.includes(query) ||
                email.includes(query) ||
                phone.includes(query)
            );
        });
    }, [contactList, searchQuery]);

    useEffect(() => {
        if (isMobileView || !contactList.length || chatTab) return;
        if (location.state?.participantId) return;
        const first = contactList[0];
        const firstId = getParticipantId(first);
        if (firstId) {
            setSelectedItem(first);
            setChatTab(firstId);
        }
    }, [contactList, chatTab, isMobileView, location.state]);

    const listItem = useMemo(
        () => contactList.find((c) => getParticipantId(c) === chatTab?.toString()),
        [contactList, chatTab]
    );

    useEffect(() => {
        if (listItem) setSelectedItem(listItem);
    }, [listItem]);

    const activeConversation =
        listItem || (getParticipantId(selectedItem) === chatTab ? selectedItem : null);

    const openChat = (participantId) => {
        if (!participantId) return;
        const id = participantId.toString();
        const item = contactList.find((c) => getParticipantId(c) === id);
        if (item) setSelectedItem(item);
        if (id === chatTabRef.current?.toString()) {
            if (isMobileView) setMobilePanel("chat");
            return;
        }
        setChatTab(id);
        if (isMobileView) setMobilePanel("chat");
    };

    const resetSelection = () => {
        setChatTab("");
        setSelectedItem(null);
        setChat([]);
        setSearchQuery("");
        setMobilePanel("list");
    };

    const handleRoleTabChange = (tab) => {
        setRoleTab(tab);
        resetSelection();
    };

    const handleQueueTabChange = (tab) => {
        setQueueTab(tab);
        resetSelection();
    };

    const handleBackToList = () => setMobilePanel("list");

    const runAction = async (action, onDone) => {
        const participantId = chatTabRef.current;
        if (!participantId || actionBusy) return;
        setActionBusy(true);
        try {
            const res = await action(participantId);
            setSession(participantId, res?.data?.data);
            onDone?.();
        } catch (err) {
            ErrorAlert(err?.response?.data?.message || "Something went wrong.");
            queryClient.invalidateQueries(["support-session", participantId]);
        } finally {
            setActionBusy(false);
            refreshLists();
        }
    };

    const handleAccept = () =>
        runAction(acceptChat, () => {
            // Keep the chat open; it now lives under "Live".
            setQueueTab("active");
        });

    const handleDecline = async () => {
        const choice = await Swal.fire({
            ...adminSwalOptions,
            title: "Decline this chat request?",
            html: `<p class="admin-swal-message">The user sees this reason and can request a new chat later.</p>`,
            input: "select",
            inputOptions: DECLINE_REASONS,
            inputPlaceholder: "Select a reason",
            inputValidator: (value) => (!value ? "Please select a reason" : undefined),
            showCancelButton: true,
            confirmButtonText: "Decline",
            cancelButtonText: "Back",
        });
        if (!choice.isConfirmed) return;

        let reason = DECLINE_REASONS[choice.value];
        if (choice.value === "other") {
            const custom = await Swal.fire({
                ...adminSwalOptions,
                title: "Reason for declining",
                input: "textarea",
                inputLabel: "Shared with the user",
                inputValidator: (value) => (!value?.trim() ? "Please enter a reason" : undefined),
                showCancelButton: true,
                confirmButtonText: "Decline",
                cancelButtonText: "Back",
            });
            if (!custom.isConfirmed) return;
            reason = custom.value.trim();
        }

        runAction((participantId) => declineChat(participantId, reason));
    };

    const handleResolve = async () => {
        const confirm = await Swal.fire({
            ...adminSwalOptions,
            title: "Close this chat?",
            html: `<p class="admin-swal-message">The user can no longer reply here. They can start a new chat request anytime.</p>`,
            showCancelButton: true,
            confirmButtonText: "Close Chat",
            cancelButtonText: "Back",
        });
        if (!confirm.isConfirmed) return;
        runAction(closeChat);
    };

    const handleMessageSent = (newMsg, statusMayHaveChanged) => {
        setChat((prev) => mergeMessages(prev, [newMsg]));
        if (statusMayHaveChanged) {
            queryClient.invalidateQueries(["support-session", chatTabRef.current]);
        }
        refreshLists();
    };

    const participantName = getConversationName(activeConversation) || "Support Chat";

    const participantRole =
        activeConversation?.user?.role === "driver"
            ? "Driver"
            : activeConversation?.user?.role === "user"
              ? "User"
              : activeConversation?.user?.role || "User";

    const participantAvatar = resolveImageUrl(
        activeConversation?.user?.profileImage,
        AVATAR_FALLBACK
    );

    const chatStatus = session?.status || activeConversation?.status || "none";
    const topic = session?.topic || activeConversation?.topic || "";
    const agentName = [session?.agent?.firstName, session?.agent?.lastName]
        .filter(Boolean)
        .join(" ");
    const isMyChat = session?.agent?._id && String(session.agent._id) === adminIdRef.current;

    let statusLabel = "Closed";
    if (chatStatus === "pending") {
        statusLabel = session?.queuePosition ? `Waiting · #${session.queuePosition} in queue` : "Waiting";
    } else if (chatStatus === "active") {
        statusLabel = isMyChat ? "Live · You" : `Live · ${agentName || "Support"}`;
    } else if (chatStatus === "declined") {
        statusLabel = "Declined";
    } else if (chatStatus === "cancelled") {
        statusLabel = "Cancelled by user";
    } else if (chatStatus === "none") {
        statusLabel = "No open chat";
    }

    const layoutClass = isMobileView
        ? mobilePanel === "chat"
            ? "premium-chat-layout is-mobile-show-chat"
            : "premium-chat-layout is-mobile-show-list"
        : "premium-chat-layout";

    const searchPlaceholder =
        roleTab === "movers" ? "Search drivers..." : roleTab === "users" ? "Search users..." : "Search...";

    const emptyListText = () => {
        if (searchQuery) return "No results found";
        if (queueTab === "pending") return "No chat requests waiting";
        if (queueTab === "active") return "No live chats right now";
        return roleTab === "movers" ? "No drivers found" : roleTab === "users" ? "No users found" : "No users found";
    };

    return (
        <div className="app-content content dashboard premium-chat-page">
            <div className="content-wrapper">
                <div className="content-body">
                    <section id="configuration">
                        <div className="user-details-wrapper">
                            {!isMobileView || mobilePanel === "list" ? (
                                <PageHeader
                                    title="Support Chat"
                                    subtitle="Users and drivers request a chat, you accept and reply live"
                                    action={
                                        counts.pending > 0 ? (
                                            <span className="premium-chat-unread-pill">
                                                {counts.pending} waiting
                                            </span>
                                        ) : null
                                    }
                                    className="premium-chat-topbar mb-0"
                                />
                            ) : null}

                            <section className="premium-chat-shell">
                                <div className={layoutClass}>
                                    <aside className="premium-chat-sidebar">
                                        <div className="premium-chat-sidebar-head">
                                            <div className="premium-queue-tabs">
                                                {QUEUE_TABS.map((tab) => {
                                                    const count =
                                                        tab.id === "pending"
                                                            ? counts.pending
                                                            : tab.id === "active"
                                                              ? counts.active
                                                              : 0;
                                                    return (
                                                        <button
                                                            key={tab.id}
                                                            type="button"
                                                            className={`premium-queue-tab ${queueTab === tab.id ? "active" : ""}`}
                                                            onClick={() => handleQueueTabChange(tab.id)}
                                                        >
                                                            {tab.label}
                                                            {count > 0 && (
                                                                <span className="premium-queue-tab-count">{count}</span>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            <div className="premium-role-tabs premium-role-tabs-3">
                                                {ROLE_TABS.map((tab) => (
                                                    <button
                                                        key={tab.id}
                                                        type="button"
                                                        className={`premium-role-tab ${roleTab === tab.id ? "active" : ""}`}
                                                        onClick={() => handleRoleTabChange(tab.id)}
                                                    >
                                                        {tab.label}
                                                    </button>
                                                ))}
                                            </div>

                                            <div className="premium-chat-search-wrap">
                                                <i className="fas fa-search premium-chat-search-icon" />
                                                <input
                                                    type="search"
                                                    className="premium-chat-search-input"
                                                    placeholder={searchPlaceholder}
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                                {searchQuery && (
                                                    <button
                                                        type="button"
                                                        className="premium-chat-search-clear"
                                                        onClick={() => setSearchQuery("")}
                                                        aria-label="Clear search"
                                                    >
                                                        <i className="fas fa-times" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="premium-chat-list custom-scrollbar">
                                            {listLoading ? (
                                                <div className="premium-chat-state">
                                                    <i className="fas fa-circle-notch fa-spin fa-2x text-primary" />
                                                </div>
                                            ) : filteredConversations.length > 0 ? (
                                                <ul className="list-unstyled m-0 p-0">
                                                    {filteredConversations.map((item) => (
                                                        <ChatList
                                                            key={`list-${getParticipantId(item)}`}
                                                            item={item}
                                                            chatTab={chatTab}
                                                            isMobileView={isMobileView}
                                                            handleTabClick={openChat}
                                                        />
                                                    ))}
                                                </ul>
                                            ) : (
                                                <div className="premium-chat-state">
                                                    <i className="fas fa-inbox fa-2x mb-2 opacity-25" />
                                                    <p className="small mb-0">{emptyListText()}</p>
                                                </div>
                                            )}
                                        </div>

                                        {isMobileView && mobilePanel === "list" && (
                                            <div className="premium-chat-mobile-hint">
                                                Tap a conversation to open chat and send messages
                                            </div>
                                        )}
                                    </aside>

                                    <section className="premium-chat-panel">
                                        {chatTab ? (
                                            <>
                                                <header className="premium-chat-panel-head">
                                                    {isMobileView && (
                                                        <button
                                                            type="button"
                                                            className="premium-chat-back-btn"
                                                            onClick={handleBackToList}
                                                            aria-label="Back to conversations"
                                                        >
                                                            <i className="fas fa-arrow-left" />
                                                        </button>
                                                    )}
                                                    <div className="premium-chat-user">
                                                        <img
                                                            src={participantAvatar}
                                                            alt={participantName}
                                                            className="premium-chat-user-avatar"
                                                            onError={(e) => {
                                                                e.currentTarget.src = AVATAR_FALLBACK;
                                                            }}
                                                        />
                                                        <div>
                                                            <h2>{participantName}</h2>
                                                            <div className="premium-chat-head-meta">
                                                                <span className="premium-chat-role-badge">
                                                                    {participantRole}
                                                                </span>
                                                                <span className={`premium-chat-status premium-chat-status-${chatStatus}`}>
                                                                    {statusLabel}
                                                                </span>
                                                                {topic && chatStatus !== "none" && (
                                                                    <span className="premium-chat-topic">{topic}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="premium-chat-head-actions">
                                                        {chatStatus === "pending" && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="premium-chat-action premium-chat-action-ghost"
                                                                    onClick={handleDecline}
                                                                    disabled={actionBusy}
                                                                >
                                                                    Decline
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="premium-chat-action premium-chat-action-primary"
                                                                    onClick={handleAccept}
                                                                    disabled={actionBusy}
                                                                >
                                                                    {actionBusy ? (
                                                                        <i className="fas fa-circle-notch fa-spin" />
                                                                    ) : (
                                                                        "Accept"
                                                                    )}
                                                                </button>
                                                            </>
                                                        )}
                                                        {chatStatus === "active" && (
                                                            <button
                                                                type="button"
                                                                className="premium-chat-action premium-chat-action-ghost"
                                                                onClick={handleResolve}
                                                                disabled={actionBusy}
                                                            >
                                                                <i className="fas fa-check me-1" />
                                                                Resolve
                                                            </button>
                                                        )}
                                                    </div>
                                                </header>

                                                <ChatMessage
                                                    key={chatTab}
                                                    chatTab={chatTab}
                                                    data={chat}
                                                    status={chatStatus}
                                                    loading={loadingMessages}
                                                    hasOlder={hasOlder}
                                                    loadingOlder={loadingOlder}
                                                    onLoadOlder={loadOlder}
                                                    onSent={handleMessageSent}
                                                />
                                            </>
                                        ) : (
                                            <div className="premium-chat-empty">
                                                <div className="premium-chat-empty-icon">
                                                    <i className="fas fa-headset" />
                                                </div>
                                                <h3>Select a conversation</h3>
                                                <p>
                                                    {queueTab === "pending"
                                                        ? "Pick a request from the queue to accept or decline it"
                                                        : isMobileView
                                                          ? "Tap a user or driver from the list to start messaging"
                                                          : "Choose a user or driver from the list to view messages"}
                                                </p>
                                            </div>
                                        )}
                                    </section>
                                </div>
                            </section>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
